import { useState, useEffect, useMemo } from "react";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays, MapPin, Clock } from "lucide-react";
import axios from "axios";

import { API } from "@/config";

const EVENT_DATE_STYLE = { backgroundColor: "#E8922F", color: "white", borderRadius: "50%" };

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(undefined);

  useEffect(() => {
    axios.get(`${API}/events`)
      .then(r => setEvents(r.data))
      .catch(err => console.error("Failed to load events:", err.message));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- API is a module constant

  const eventDates = useMemo(() => events.map(e => new Date(e.date + "T00:00:00")), [events]);
  const calendarModifiers = useMemo(() => ({ event: eventDates }), [eventDates]);
  const calendarModifierStyles = useMemo(() => ({ event: EVENT_DATE_STYLE }), []);

  const filteredEvents = selectedDate
    ? events.filter(e => {
        const eventDate = new Date(e.date + "T00:00:00");
        return eventDate.toDateString() === selectedDate.toDateString();
      })
    : events;

  return (
    <div data-testid="events-page">
      {/* Hero */}
      <section className="bg-[#1A1A1A] py-20 md:py-28">
        <div className="section-container text-center">
          <p className="text-[#E8922F] text-sm uppercase tracking-widest font-semibold mb-3">Upcoming</p>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Events & Calendar
          </h1>
          <p className="text-gray-400 text-base md:text-lg mt-4 max-w-xl mx-auto">
            Stay updated with our rehearsals, performances, and community events.
          </p>
        </div>
      </section>

      {/* Calendar + Events */}
      <section className="section-padding bg-[#FAFAFA]">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8">
            {/* Calendar */}
            <div className="lg:col-span-5">
              <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm" data-testid="events-calendar">
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Calendar
                </h3>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  modifiers={calendarModifiers}
                  modifiersStyles={calendarModifierStyles}
                  className="rounded-xl"
                />
                {selectedDate && (
                  <button
                    onClick={() => setSelectedDate(undefined)}
                    className="text-sm text-[#C4392D] mt-3 hover:underline"
                    data-testid="clear-date-filter"
                  >
                    Clear date filter
                  </button>
                )}
              </div>
            </div>

            {/* Events List */}
            <div className="lg:col-span-7" data-testid="events-list">
              <h3 className="text-xl font-bold text-[#1A1A1A] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {selectedDate ? `Events on ${selectedDate.toLocaleDateString()}` : "All Upcoming Events"}
              </h3>
              {filteredEvents.length === 0 ? (
                <div className="bg-white border border-gray-100 rounded-2xl p-8 text-center">
                  <CalendarDays size={40} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-[#4A4A4A]">No events {selectedDate ? "on this date" : "scheduled yet"}.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredEvents.map((event) => (
                    <div
                      key={event.id}
                      className="bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-shadow rounded-2xl p-6"
                      data-testid={`event-item-${event.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 text-[#E8922F] mb-2">
                            <CalendarDays size={14} />
                            <span className="text-sm font-medium">{event.date}</span>
                          </div>
                          <h4 className="text-xl font-bold text-[#1A1A1A] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                            {event.title}
                          </h4>
                          <p className="text-[#4A4A4A] text-sm mb-3">{event.description}</p>
                          <div className="flex flex-wrap gap-4 text-xs text-[#4A4A4A]">
                            {event.time && (
                              <span className="flex items-center gap-1 bg-[#FFF3E6] px-3 py-1 rounded-full">
                                <Clock size={12} className="text-[#E8922F]" />{event.time}
                              </span>
                            )}
                            {event.location && (
                              <span className="flex items-center gap-1 bg-[#FFF3E6] px-3 py-1 rounded-full">
                                <MapPin size={12} className="text-[#E8922F]" />{event.location}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
